// Flow 3 continued - mockup review + the SECOND, load-bearing default-deny approval
// gate (docs/phase-0/slice5-ux-flows.md §6.3-6.4; route `/apps/new/brand/mockups`).
// D32: "Build proceeds only on approval." Approving navigates to the guided
// workflow-validation walkthrough (Flow 4).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { AppView } from "@nightcrew/shared";
import { FocusedFlowShell } from "../../components/FocusedFlowShell.js";
import { BrandMockupCard } from "../../components/BrandMockupCard.js";
import { BrandApprovalGate } from "../../components/BrandApprovalGate.js";
import { generateMockups, approveMockups, tweakMockup } from "../../api/brand.js";
import { getApp } from "../../api/apps.js";
import { buildCopy } from "../../copy/build.js";
import { isViewerRole, useSession } from "../../state/session.js";
import { useWizard } from "../../state/wizard.js";
import { canEnterBrand } from "./gate.js";
import { getStoredBrandDirectionId } from "./brand-session.js";

type ScreenState =
  | { readonly kind: "redirect" }
  | { readonly kind: "loading_app" }
  | { readonly kind: "app_error" }
  | { readonly kind: "ready"; readonly app: AppView };

type MockupsState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | { readonly kind: "results"; readonly screens: readonly string[] }
  | { readonly kind: "approved" };

export const BrandMockupReview = (): React.JSX.Element => {
  const { state: session } = useSession();
  const { draft } = useWizard();
  const navigate = useNavigate();
  const appId = draft.appId;
  const directionId = getStoredBrandDirectionId();

  const [screenState, setScreenState] = useState<ScreenState>({ kind: "loading_app" });
  const [mockupsState, setMockupsState] = useState<MockupsState>({ kind: "loading" });
  const [tweakingScreen, setTweakingScreen] = useState<string | null>(null);

  useEffect(() => {
    if (appId === null || directionId === null) return;
    const cancelled = { current: false };
    void (async () => {
      const result = await getApp(appId);
      if (cancelled.current) return;
      if (isErr(result)) {
        setScreenState({ kind: "app_error" });
        return;
      }
      setScreenState(canEnterBrand(result.value.verdict) ? { kind: "ready", app: result.value } : { kind: "redirect" });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [appId, directionId]);

  useEffect(() => {
    if (screenState.kind !== "ready" || appId === null || directionId === null) return;
    const cancelled = { current: false };
    setMockupsState({ kind: "loading" });
    void (async () => {
      const result = await generateMockups(appId, directionId);
      if (cancelled.current) return;
      setMockupsState(isErr(result) ? { kind: "error" } : { kind: "results", screens: result.value.screens });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [screenState.kind, appId, directionId]);

  if (appId === null || directionId === null || screenState.kind === "redirect") {
    return <MockupsRedirect />;
  }

  if (screenState.kind === "loading_app") {
    return (
      <FocusedFlowShell title={buildCopy("build.brand.mockups.title")}>
        <div className="card pad skeleton-card" />
      </FocusedFlowShell>
    );
  }

  if (screenState.kind === "app_error") {
    return (
      <FocusedFlowShell title={buildCopy("build.brand.mockups.title")}>
        <div className="card state-card">
          <p>We couldn&apos;t load this app. Try again.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setScreenState({ kind: "loading_app" });
            }}
          >
            Retry
          </button>
        </div>
      </FocusedFlowShell>
    );
  }

  const { app } = screenState;
  const viewer = isViewerRole(session);
  const viewerReason = viewer ? buildCopy("build.brand.mockups.deniedRole") : undefined;

  const handleTweak = (screenRef: string, instruction: string): void => {
    setTweakingScreen(screenRef);
    void (async () => {
      await tweakMockup(app.id, directionId, screenRef, instruction);
      setTweakingScreen(null);
    })();
  };

  return (
    <FocusedFlowShell
      title={buildCopy("build.brand.mockups.title")}
      onBack={() => {
        void navigate("/apps/new/brand");
      }}
    >
      <h1 className="h2">{buildCopy("build.brand.mockups.title")}</h1>

      {mockupsState.kind === "loading" ? (
        <div className="mt16">
          <div className="meter bordered">
            <span style={{ width: "60%" }} />
          </div>
          <p className="muted mt8" role="status" style={{ fontSize: 12.5 }}>
            {buildCopy("build.brand.mockups.loading", { appName: app.name })}
          </p>
        </div>
      ) : null}

      {mockupsState.kind === "error" ? (
        <div className="card state-card mt16">
          <p>{buildCopy("build.brand.mockups.error")}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setMockupsState({ kind: "loading" });
            }}
          >
            {buildCopy("build.errors.generic.retryCta")}
          </button>
        </div>
      ) : null}

      {mockupsState.kind === "results" ? (
        <>
          <div className="grid mt16" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
            {mockupsState.screens.map((screenRef) => (
              <BrandMockupCard
                key={screenRef}
                screenRef={screenRef}
                busy={tweakingScreen === screenRef}
                onTweak={(instruction) => {
                  handleTweak(screenRef, instruction);
                }}
                {...(viewerReason !== undefined ? { disabledReason: viewerReason } : {})}
              />
            ))}
          </div>
          <div className="mt16">
            <BrandApprovalGate
              appName={app.name}
              {...(viewerReason !== undefined ? { disabledReason: viewerReason } : {})}
              {...(tweakingScreen !== null
                ? { tweakInFlightReason: buildCopy("build.brand.mockups.gate.tweakInFlightNote") }
                : {})}
              onApprove={() => {
                void (async () => {
                  const result = await approveMockups(app.id, directionId);
                  if (isErr(result)) return;
                  setMockupsState({ kind: "approved" });
                  void navigate("/apps/new/build/workflow");
                })();
              }}
              onKeepRefining={() => {
                // Stays on this screen - nothing to do; the gallery is already visible.
              }}
            />
          </div>
        </>
      ) : null}

      {mockupsState.kind === "approved" ? (
        <div className="card pad mt16" role="status">
          <p>{buildCopy("build.brand.mockups.approvedConfirmation", { appName: app.name })}</p>
        </div>
      ) : null}
    </FocusedFlowShell>
  );
};

const MockupsRedirect = (): React.JSX.Element => {
  const navigate = useNavigate();
  useEffect(() => {
    void navigate("/apps/new/brand", { replace: true });
  }, [navigate]);
  return <FocusedFlowShell title={buildCopy("build.brand.mockups.title")}>{null}</FocusedFlowShell>;
};
